import { useMutation, useQuery } from '@apollo/client';
import { CancelOutlined, DeleteOutlined, EditNoteOutlined, RestartAlt } from '@mui/icons-material';
import { ListItemIcon, ListItemText, Menu, MenuItem, MenuProps, PopoverOrigin, Tooltip } from '@mui/material';
import { ConversationSettingsConfig } from '@procureai/agents';
import { Permission } from '@procureai/permissions';
import { Restricted, usePermissions } from '@procureai/permissions-ui';
import { useConfirmationDialog, WrapWith, useCustomSnackbar } from '@procureai/ui-mui';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Union } from 'ts-toolbelt';

import {
  SvbrgConversationSettingsCreateMutation,
  SvbrgConversationEntityVariant,
  SvbrgConversationSettingsUpdateMutation,
  TsrctDemandStatus,
} from '@/generated/gql/graphql';
import { ConversationSettingsFormDialog } from '~/components/ConversationSettingsFormDialog/ConversationSettingsFormDialog';
import { useConversationSettingsDialog } from '~/components/ConversationSettingsFormDialog/ConversationSettingsFormDialog.hooks';
import { defaultConversationSettings } from '~/components/ConversationSettingsFormDialog/schema';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';
import { RouteId } from '~/routes';

import {
  transformCreateQuickActionsToGraphQl,
  transformSettingsToFormData,
  transformToolsToGraphQl,
} from './DemandConversationSettings.utils';
import { DEMAND_CONVERSATION_SETTINGS_CREATE_MUTATION } from './DemandConversationSettingsCreate.gql';
import { DEMAND_CONVERSATION_SETTINGS_QUERY } from './DemandConversationSettingsQuery.gql';
import { DEMAND_CONVERSATION_SETTINGS_UPDATE_MUTATION } from './DemandConversationSettingsUpdate.gql';
import { NegotiationDemandCancelDialog } from './NegotiationDemandCancelDialog';
import { NEGOTIATION_DEMAND_DELETE_MUTATION } from './NegotiationDemandDelete.gql';
import { NEGOTIATION_DEMAND_RESET_MUTATION } from './NegotiationDemandReset.gql';

const ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'right',
};
const TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
};

export interface ActionsMenuProps extends MenuProps {
  id: string;
  status: TsrctDemandStatus;
  permissions?: Union.Nullable<{ write: boolean }>;
}

const cancellableStatusMap: Record<TsrctDemandStatus, boolean> = {
  INITIALIZED: true,
  OPEN: true,
  IN_PROGRESS: true,
  COMPLETED: false,
  PO_PENDING: false,
  UPSTREAM_DELETED: false,
  CANCELLED: false,
};

export const ActionsMenu = ({ id, status, permissions, ...otherProps }: ActionsMenuProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'NegotiationDemandDetail.action' });
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const { confirm } = useConfirmationDialog();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const track = useTrackInteraction();
  const { closeDialog, openDialog, isOpen } = useConversationSettingsDialog();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const [reset] = useMutation(NEGOTIATION_DEMAND_RESET_MUTATION, {
    onError,
  });
  const [del] = useMutation(NEGOTIATION_DEMAND_DELETE_MUTATION, {
    onError,
  });
  const [createConversationSettings, { loading: createLoading }] = useMutation(
    DEMAND_CONVERSATION_SETTINGS_CREATE_MUTATION,
    {
      onError,
      refetchQueries: [
        {
          query: DEMAND_CONVERSATION_SETTINGS_QUERY,
          variables: {
            entityId: id,
            entityVariant: SvbrgConversationEntityVariant.DEMAND,
          },
        },
      ],
    },
  );
  const [updateConversationSettings, { loading: updateLoading }] = useMutation(
    DEMAND_CONVERSATION_SETTINGS_UPDATE_MUTATION,
    {
      onError,
      refetchQueries: [
        {
          query: DEMAND_CONVERSATION_SETTINGS_QUERY,
          variables: {
            entityId: id,
            entityVariant: SvbrgConversationEntityVariant.DEMAND,
          },
        },
      ],
    },
  );
  const submitting = createLoading || updateLoading;

  const { data, loading } = useQuery(DEMAND_CONVERSATION_SETTINGS_QUERY, {
    variables: {
      entityId: id,
      entityVariant: SvbrgConversationEntityVariant.DEMAND,
    },
    // Requesting ConversationSettings requires the use permission explicitly
    skip: !hasPermission(Permission.ConversationDemandUse),
  });

  const existingSettings = data?.sevenbridges?.conversationSettings;

  const conversationSettingsData = useMemo(() => transformSettingsToFormData(existingSettings), [existingSettings]);

  const hasAnyFeaturePermission = hasPermission({
    any: [
      Permission.DemandReset,
      Permission.DemandCancel,
      Permission.DemandDelete,
      Permission.ConversationDemandManage,
    ],
  });
  const canWrite = hasAnyFeaturePermission && permissions?.write;

  const handleSaveConversationSettings = async (formData: ConversationSettingsConfig) => {
    try {
      const tools = formData.tools;
      const quickActions = transformCreateQuickActionsToGraphQl(formData.quickActions);

      if (existingSettings) {
        const updateData: SvbrgConversationSettingsUpdateMutation = {
          id: existingSettings.id,
          entityId: id,
          entityVariant: SvbrgConversationEntityVariant.DEMAND,
          tools: transformToolsToGraphQl(tools),
          quickActions,
        };

        const { errors } = await updateConversationSettings({
          variables: { data: updateData },
        });

        if (!errors) {
          enqueueSuccessSnackbar(t('manageConversation.update.success'));
        }
      } else {
        const createData: SvbrgConversationSettingsCreateMutation = {
          entityId: id,
          entityVariant: SvbrgConversationEntityVariant.DEMAND,
          tools: transformToolsToGraphQl(tools),
          quickActions,
        };

        const { errors } = await createConversationSettings({
          variables: { data: createData },
        });

        if (!errors) {
          enqueueSuccessSnackbar(t('manageConversation.create.success'));
        }
      }

      closeDialog();
    } catch (error) {
      onError(error as Error);
    }
  };

  return (
    <>
      <ConversationSettingsFormDialog
        open={isOpen}
        onClose={closeDialog}
        onSave={handleSaveConversationSettings}
        defaultValues={defaultConversationSettings}
        loading={loading}
        submitting={submitting}
        data={conversationSettingsData}
      />
      <NegotiationDemandCancelDialog
        demandId={id}
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onCancelled={() => track(Target.DEMAND, Action.CANCELLED, { id })}
      />
      <Menu {...otherProps} anchorOrigin={ANCHOR_ORIGIN} transformOrigin={TRANSFORM_ORIGIN}>
        <WrapWith component={Tooltip} condition={!canWrite} title={t(`remove.noPermission`)} placement="left">
          <div>
            <Restricted needs={{ permission: Permission.ConversationDemandManage }}>
              <MenuItem
                data-intercom-target="NegotiationDemandDetail/Actions/Conversation"
                disabled={!canWrite}
                onClick={openDialog}
              >
                <ListItemIcon>
                  <EditNoteOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('manageConversation.label')} />
              </MenuItem>
            </Restricted>
            <Restricted needs={{ permission: Permission.DemandReset }}>
              <MenuItem
                data-intercom-target="NegotiationDemandDetail/Actions/Reset"
                disabled={!canWrite}
                onClick={confirm(
                  async () => {
                    const { errors } = await reset({
                      variables: {
                        id,
                      },
                    });
                    if (!errors) {
                      enqueueSuccessSnackbar(t(`reset.success`));
                      track(Target.DEMAND, Action.RESET, { id });
                    }
                  },
                  {
                    message: <Trans t={t} i18nKey="reset.confirm" />,
                  },
                )}
              >
                <ListItemIcon>
                  <RestartAlt fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('reset.label')} />
              </MenuItem>
            </Restricted>
            <Restricted needs={{ permission: Permission.DemandCancel }}>
              <MenuItem
                data-intercom-target="NegotiationDemandDetail/Actions/Cancel"
                disabled={!canWrite || !cancellableStatusMap[status]}
                onClick={() => setCancelDialogOpen(true)}
              >
                <ListItemIcon>
                  <CancelOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('cancel.label')} />
              </MenuItem>
            </Restricted>
            <Restricted needs={{ permission: Permission.DemandDelete }}>
              <MenuItem
                data-intercom-target="NegotiationDemandDetail/Actions/Delete"
                disabled={!canWrite}
                onClick={confirm(
                  async () => {
                    const { errors } = await del({
                      variables: {
                        id,
                      },
                    });
                    if (!errors) {
                      enqueueSuccessSnackbar(t(`remove.success`));
                      track(Target.DEMAND, Action.DELETED, { id });
                      navigate(RouteId.demand);
                    }
                  },
                  {
                    message: <Trans t={t} i18nKey="remove.confirm" />,
                  },
                )}
              >
                <ListItemIcon>
                  <DeleteOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('remove.label')} />
              </MenuItem>
            </Restricted>
          </div>
        </WrapWith>
      </Menu>
    </>
  );
};
